import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { CheckCircle2, Calendar, Users, ClipboardCheck, TrendingUp, X, ArrowRight, ArrowLeft } from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    step: 1,
    title: 'Welcome to Athlete Attendance App!',
    description: 'Let\'s get you started with a quick tour of the key features.',
    icon: CheckCircle2,
    color: '#0067B9',
    content: (
      <div className="space-y-4">
        <p className="text-gray-700">
          This tutorial will guide you through the essential features of the Athlete Attendance App Attendance Management system:
        </p>
        <ul className="space-y-3">
          <li className="flex items-start space-x-3">
            <Calendar className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>Programs:</strong> Create and manage your sports programs</span>
          </li>
          <li className="flex items-start space-x-3">
            <Users className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <span><strong>Athletes:</strong> Add athletes and assign them to programs</span>
          </li>
          <li className="flex items-start space-x-3">
            <ClipboardCheck className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <span><strong>Attendance:</strong> Track daily attendance efficiently</span>
          </li>
          <li className="flex items-start space-x-3">
            <TrendingUp className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <span><strong>Reports:</strong> Generate insights and export data</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    step: 2,
    title: 'Step 1: Create a Program',
    description: 'Start by creating your first sports program.',
    icon: Calendar,
    color: '#0067B9',
    action: { label: 'Go to Programs', path: '/programs' },
    content: (
      <div className="space-y-4">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <p className="font-semibold text-blue-900 mb-2">What is a Program?</p>
          <p className="text-blue-800 text-sm">
            A program represents a sports team or training group (e.g., "Basketball U12", "Soccer Training").
          </p>
        </div>
        
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">To create a program:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Click "Programs" in the navigation menu</li>
            <li>Click the "Add Program" button</li>
            <li>Fill in program details:
              <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                <li>Program name (e.g., "Commonwealth 3x3 Men")</li>
                <li>Sport type</li>
                <li>Schedule and time</li>
                <li>Choose a color theme</li>
              </ul>
            </li>
            <li>Click "Create Program"</li>
          </ol>
        </div>
      </div>
    )
  },
  {
    step: 3,
    title: 'Step 2: Add Athletes',
    description: 'Add athletes to your roster and assign them to programs.',
    icon: Users,
    color: '#007A3D',
    action: { label: 'Go to Athletes', path: '/athletes' },
    content: (
      <div className="space-y-4">
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <p className="font-semibold text-green-900 mb-2">Two Ways to Add Athletes:</p>
          <ul className="text-green-800 text-sm space-y-1">
            <li>• <strong>Manually:</strong> Add one athlete at a time</li>
            <li>• <strong>CSV Import:</strong> Upload multiple athletes at once</li>
          </ul>
        </div>
        
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">Manual Entry:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Click "Athletes" in the navigation menu</li>
            <li>Click "Add Athlete"</li>
            <li>Enter athlete details:
              <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                <li>Full name</li>
                <li>Date of birth (age auto-calculated)</li>
                <li>Contact information</li>
                <li>Assign to program(s)</li>
              </ul>
            </li>
          </ol>
        </div>
        
        <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
          <p className="font-semibold mb-1">💡 Pro Tip:</p>
          <p>Use "Import CSV" for bulk uploads! Download the template, fill it out, and upload.</p>
        </div>
      </div>
    )
  },
  {
    step: 4,
    title: 'Step 3: Mark Attendance',
    description: 'Track who attends each training session.',
    icon: ClipboardCheck,
    color: '#FCD116',
    action: { label: 'Go to Attendance', path: '/attendance' },
    content: (
      <div className="space-y-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
          <p className="font-semibold text-yellow-900 mb-2">Quick & Easy Attendance</p>
          <p className="text-yellow-800 text-sm">
            Mark attendance with just one click per athlete. Add notes when needed.
          </p>
        </div>
        
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">Taking Attendance:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Click "Attendance" in the navigation menu</li>
            <li>Select the program and date</li>
            <li>For each athlete, click their status:
              <div className="flex gap-2 mt-2 ml-6 flex-wrap">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">Present</span>
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Absent</span>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Late</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">Excused</span>
              </div>
            </li>
            <li>Add optional notes for any athlete</li>
            <li>Click "Save Attendance"</li>
          </ol>
        </div>
      </div>
    )
  },
  {
    step: 5,
    title: 'Step 4: Generate Reports',
    description: 'View analytics and export data for your records.',
    icon: TrendingUp,
    color: '#CE1126',
    action: { label: 'Go to Reports', path: '/reports' },
    content: (
      <div className="space-y-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="font-semibold text-red-900 mb-2">Powerful Reporting Tools</p>
          <p className="text-red-800 text-sm">
            Track attendance rates, identify trends, and export data for sharing.
          </p>
        </div>
        
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">Available Reports:</p>
          <ul className="space-y-2 text-gray-700 ml-2">
            <li className="flex items-start space-x-2">
              <span className="text-blue-600 font-bold">1.</span>
              <div>
                <strong>Attendance Report:</strong>
                <p className="text-sm">Overall statistics, attendance breakdown, and athlete performance</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-600 font-bold">2.</span>
              <div>
                <strong>Athletes by Program:</strong>
                <p className="text-sm">Complete roster with contact details grouped by program</p>
              </div>
            </li>
          </ul>
        </div>
        
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">Filter & Export:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2 text-sm">
            <li>Filter by program and date range</li>
            <li>View visual charts and statistics</li>
            <li>Export to CSV for Excel or Google Sheets</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    step: 6,
    title: 'You\'re All Set!',
    description: 'Start managing your athlete attendance today.',
    icon: CheckCircle2,
    color: '#007A3D',
    content: (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg text-center">
          <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Congratulations!</h3>
          <p className="text-gray-700">
            You're ready to start using Athlete Attendance App Attendance Management.
          </p>
        </div>
        
        <div className="space-y-3">
          <p className="font-semibold text-gray-800">Quick Tips:</p>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Access this tutorial anytime from the Dashboard "Help" button</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Use the logout button in the top navigation when finished</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>Your data is securely saved and accessible anytime you log in</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-blue-600">•</span>
              <span>For support, contact: info@attendancetracker.ws</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <p className="text-blue-900 font-semibold">Ready to get started?</p>
          <p className="text-blue-700 text-sm mt-1">Click "Finish Tutorial" to begin!</p>
        </div>
      </div>
    )
  }
];

const Tutorial = ({ isOpen, onClose, autoShow = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  
  const step = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      handleFinish();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onClose();
  };

  const handleFinish = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onClose();
  };

  const handleActionClick = () => {
    if (step.action) {
      onClose();
      navigate(step.action.path);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${step.color}20` }}
              >
                <Icon className="h-6 w-6" style={{ color: step.color }} />
              </div>
              <div>
                <DialogTitle className="text-xl">{step.title}</DialogTitle>
                <p className="text-sm text-gray-600 mt-1">{step.description}</p>
              </div>
            </div>
            {!autoShow && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center space-x-2 mb-6">
          {TUTORIAL_STEPS.map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-2 rounded-full transition-all ${
                index <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="py-4">
          {step.content}
        </div>

        {/* Action button if available */}
        {step.action && (
          <Button
            onClick={handleActionClick}
            className="w-full mb-4"
            style={{ backgroundColor: step.color }}
          >
            {step.action.label}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="text-sm text-gray-600">
            Step {currentStep + 1} of {TUTORIAL_STEPS.length}
          </div>

          <div className="flex space-x-2">
            {!isLastStep && (
              <Button
                variant="outline"
                onClick={handleSkip}
              >
                Skip Tutorial
              </Button>
            )}
            <Button
              onClick={handleNext}
              style={{ backgroundColor: '#0067B9' }}
            >
              {isLastStep ? 'Finish Tutorial' : 'Next'}
              {!isLastStep && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Tutorial;
